import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { addLocale, locale } from 'primereact/api'

import { SupportedLanguage, supportedLanguages } from '../../utils/i18n'

/**
 * Provides localization support for PrimeReact components based on the current i18next language.
 * This component uses the PrimeReact API to set the locale and add localized strings for various
 * PrimeReact features.
 *
 * @component
 * @example
 * // Example usage:
 * // Wrap your application with PrimeReactLocalsProvider to enable PrimeReact localization.
 * <PrimeReactLocalsProvider>
 *  <PrimeReactProvider>
 *    <App />
 *  </PrimeReactProvider>
 * </PrimeReactLocalsProvider>
 *
 * @param {JSX.Element} props.children - Child elements to be rendered within the provider.
 *
 * @returns {JSX.Element} React component with localized PrimeReact support.
 */

type Props = {
  children: JSX.Element
}

export const PrimeReactLocalsProvider: React.FC<Props> = ({ children }) => {
  const { t, i18n } = useTranslation('primelocale')
  const { language } = i18n

  // Retrieve localized strings for PrimeReact components
  // Get newest translations or languages from here https://github.com/primefaces/primelocale
  const getPrimeLocal = (language: SupportedLanguage) => {
    return {
      accept: t('accept', { lng: language }),
      addRule: t('addRule', { lng: language }),
      am: t('am', { lng: language }),
      apply: t('apply', { lng: language }),
      cancel: t('cancel', { lng: language }),
      choose: t('choose', { lng: language }),
      chooseDate: t('chooseDate', { lng: language }),
      chooseMonth: t('chooseMonth', { lng: language }),
      chooseYear: t('chooseYear', { lng: language }),
      clear: t('clear', { lng: language }),
      completed: t('completed', { lng: language }),
      contains: t('contains', { lng: language }),
      custom: t('custom', { lng: language }),
      dateAfter: t('dateAfter', { lng: language }),
      dateBefore: t('dateBefore', { lng: language }),
      dateFormat: t('dateFormat', { lng: language }),
      dateIs: t('dateIs', { lng: language }),
      dateIsNot: t('dateIsNot', { lng: language }),
      dayNames: t('dayNames', {
        lng: language,
        returnObjects: true,
      }) as string[],
      dayNamesMin: t('dayNamesMin', {
        lng: language,
        returnObjects: true,
      }) as string[],
      dayNamesShort: t('dayNamesShort', {
        lng: language,
        returnObjects: true,
      }) as string[],
      emptyFilterMessage: t('emptyFilterMessage', { lng: language }),
      emptyMessage: t('emptyMessage', { lng: language }),
      emptySearchMessage: t('emptySearchMessage', { lng: language }),
      emptySelectionMessage: t('emptySelectionMessage', { lng: language }),
      endsWith: t('endsWith', { lng: language }),
      equals: t('equals', { lng: language }),
      fileSizeTypes: t('fileSizeTypes', {
        lng: language,
        returnObjects: true,
      }) as string[],
      filter: t('filter', { lng: language }),
      firstDayOfWeek: parseInt(t('firstDayOfWeek', { lng: language })),
      gt: t('gt', { lng: language }),
      gte: t('gte', { lng: language }),
      lt: t('lt', { lng: language }),
      lte: t('lte', { lng: language }),
      matchAll: t('matchAll', { lng: language }),
      matchAny: t('matchAny', { lng: language }),
      medium: t('medium', { lng: language }),
      monthNames: t('monthNames', {
        lng: language,
        returnObjects: true,
      }) as string[],
      monthNamesShort: t('monthNamesShort', {
        lng: language,
        returnObjects: true,
      }) as string[],
      nextDecade: t('nextDecade', { lng: language }),
      nextHour: t('nextHour', { lng: language }),
      nextMinute: t('nextMinute', { lng: language }),
      nextMonth: t('nextMonth', { lng: language }),
      nextSecond: t('nextSecond', { lng: language }),
      nextYear: t('nextYear', { lng: language }),
      noFilter: t('noFilter', { lng: language }),
      notContains: t('notContains', { lng: language }),
      notEquals: t('notEquals', { lng: language }),
      now: t('now', { lng: language }),
      passwordPrompt: t('passwordPrompt', { lng: language }),
      pending: t('pending', { lng: language }),
      pm: t('pm', { lng: language }),
      prevDecade: t('prevDecade', { lng: language }),
      prevHour: t('prevHour', { lng: language }),
      prevMinute: t('prevMinute', { lng: language }),
      prevMonth: t('prevMonth', { lng: language }),
      prevSecond: t('prevSecond', { lng: language }),
      prevYear: t('prevYear', { lng: language }),
      reject: t('reject', { lng: language }),
      removeRule: t('removeRule', { lng: language }),
      searchMessage: t('searchMessage', { lng: language }),
      selectionMessage: t('selectionMessage', { lng: language }),
      showMonthAfterYear: t('showMonthAfterYear', { lng: language }),
      startsWith: t('startsWith', { lng: language }),
      strong: t('strong', { lng: language }),
      today: t('today', { lng: language }),
      upload: t('upload', { lng: language }),
      weak: t('weak', { lng: language }),
      weekHeader: t('weekHeader', { lng: language }),
      aria: {
        cancelEdit: t('aria.cancelEdit', { lng: language }),
        close: t('aria.close', { lng: language }),
        // @ts-ignore
        collapseLabel: t('aria.collapseLabel', { lng: language }),
        collapseRow: t('aria.collapseRow', { lng: language }),
        editRow: t('aria.editRow', { lng: language }),
        expandLabel: t('aria.expandLabel', { lng: language }),
        expandRow: t('aria.expandRow', { lng: language }),
        falseLabel: t('aria.falseLabel', { lng: language }),
        filterConstraint: t('aria.filterConstraint', { lng: language }),
        filterOperator: t('aria.filterOperator', { lng: language }),
        firstPageLabel: t('aria.firstPageLabel', { lng: language }),
        gridView: t('aria.gridView', { lng: language }),
        hideFilterMenu: t('aria.hideFilterMenu', { lng: language }),
        jumpToPageDropdownLabel: t('aria.jumpToPageDropdownLabel', {
          lng: language,
        }),
        jumpToPageInputLabel: t('aria.jumpToPageInputLabel', { lng: language }),
        lastPageLabel: t('aria.lastPageLabel', { lng: language }),
        listView: t('aria.listView', { lng: language }),
        moveAllToSource: t('aria.moveAllToSource', { lng: language }),
        moveAllToTarget: t('aria.moveAllToTarget', { lng: language }),
        moveBottom: t('aria.moveBottom', { lng: language }),
        moveDown: t('aria.moveDown', { lng: language }),
        moveTop: t('aria.moveTop', { lng: language }),
        moveToSource: t('aria.moveToSource', { lng: language }),
        moveToTarget: t('aria.moveToTarget', { lng: language }),
        moveUp: t('aria.moveUp', { lng: language }),
        navigation: t('aria.navigation', { lng: language }),
        next: t('aria.next', { lng: language }),
        nextPageLabel: t('aria.nextPageLabel', { lng: language }),
        nullLabel: t('aria.nullLabel', { lng: language }),
        otpLabel: t('aria.otpLabel', { lng: language }),
        pageLabel: t('aria.pageLabel', { lng: language }),
        passwordHide: t('aria.passwordHide', { lng: language }),
        passwordShow: t('aria.passwordShow', { lng: language }),
        previous: t('aria.previous', { lng: language }),
        previousPageLabel: t('aria.previousPageLabel', { lng: language }),
        removeLabel: t('aria.removeLabel', { lng: language }),
        rotateLeft: t('aria.rotateLeft', { lng: language }),
        rotateRight: t('aria.rotateRight', { lng: language }),
        rowsPerPageLabel: t('aria.rowsPerPageLabel', { lng: language }),
        saveEdit: t('aria.saveEdit', { lng: language }),
        scrollTop: t('aria.scrollTop', { lng: language }),
        selectAll: t('aria.selectAll', { lng: language }),
        selectLabel: t('aria.selectLabel', { lng: language }),
        selectRow: t('aria.selectRow', { lng: language }),
        showFilterMenu: t('aria.showFilterMenu', { lng: language }),
        slide: t('aria.slide', { lng: language }),
        slideNumber: t('aria.slideNumber', { lng: language }),
        star: t('aria.star', { lng: language }),
        stars: t('aria.stars', { lng: language }),
        trueLabel: t('aria.trueLabel', { lng: language }),
        unselectAll: t('aria.unselectAll', { lng: language }),
        unselectLabel: t('aria.unselectLabel', { lng: language }),
        unselectRow: t('aria.unselectRow', { lng: language }),
        zoomImage: t('aria.zoomImage', { lng: language }),
        zoomIn: t('aria.zoomIn', { lng: language }),
        zoomOut: t('aria.zoomOut', { lng: language }),
      },
    }
  }

  useEffect(() => {
    // Change PrimeReact language in UI
    locale(language)
  }, [language])

  // Add languages to PrimeReact
  supportedLanguages.forEach(language => {
    addLocale(language, getPrimeLocal(language))
  })

  return <>{children}</>
}
